import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuidedBookingContext } from '@contexts/GuidedBookingContext';
import { useBranding } from '@utils/api';
import { Button } from '@/components/ui/button';

interface WizardNavigationProps {
  language: string;
}

const WizardNavigation: FC<WizardNavigationProps> = ({ language }) => {
  const { t } = useTranslation();
  const { branding } = useBranding();
  const { 
    currentStep, 
    canGoBack, 
    goBack, 
    goForward,
    isCurrentStep,
    bookingData,
    restart,
    submitAppointment,
    isSubmitting
  } = useGuidedBookingContext();

  const handleNext = async () => {
    if (isCurrentStep('appointmentSelection')) {
      // Move to booking confirmation
      goForward('bookingConfirmation');
    } else if (isCurrentStep('bookingConfirmation')) {
      // Submit the appointment
      await submitAppointment();
    }
  };

  const handleBack = () => {
    if (isCurrentStep('appointmentSelection')) {
      restart();
    } else {
      goBack();
    }
  };

  if (isCurrentStep('complete')) {
    return null; // No navigation needed on completion step
  }

  return (
    <div className="flex justify-center gap-4 pt-4">
      {canGoBack && (
        <Button 
          variant="outline"
          size="sm"
          onClick={handleBack}
          style={{
            backgroundColor: branding?.buttonColor ? `${branding.buttonColor}1A` : 'transparent',
            borderColor: branding?.buttonColor || '#d1d5db',
          }}
          className="hover:shadow-md hover:-translate-y-0.5 transition-all"
        >
          {isCurrentStep('appointmentSelection') ? t("booking.buttons.restart") : t("booking.buttons.back")}
        </Button>
      )}

      {isCurrentStep('appointmentSelection') && bookingData.scheduledAt && (
        <Button 
          size="sm"
          onClick={handleNext}
          style={{
            backgroundColor: branding?.buttonColor || '#3b82f6',
            color: branding?.fontColor || '#ffffff',
          }}
          className="hover:shadow-md hover:-translate-y-0.5 transition-all"
        >
          {t("booking.buttons.next")}
        </Button>
      )}

      {isCurrentStep('bookingConfirmation') && (
        <Button 
          size="sm"
          onClick={handleNext}
          loading={isSubmitting}
          style={{
            backgroundColor: branding?.buttonColor || '#3b82f6',
            color: branding?.fontColor || '#ffffff',
          }}
          className="hover:shadow-md hover:-translate-y-0.5 transition-all"
        >
          {t("booking.buttons.requestAppointment")}
        </Button>
      )}
    </div>
  );
};

export default WizardNavigation;